import { useState } from "react";
import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { authApi } from "@/api/auth";
import { tokenStore } from "@/auth/token-store";
import { markSignedIn } from "@/auth/session";
import { useSessionStore } from "@/stores/session";
import { BrandMark } from "@/components/display/brand-mark";
import { Form } from "@/components/form/form";
import { FormTextField } from "@/components/form/fields";

export const Route = createFileRoute("/_auth/setup")({
  beforeLoad: async () => {
    const status = await authApi.setupStatus();
    if (!status.needsSetup) throw redirect({ to: "/login" });
  },
  component: SetupPage,
});

const schema = z
  .object({
    displayName: z.string().min(1, "Required"),
    email: z.string().email("Enter a valid email"),
    password: z.string().min(8, "At least 8 characters"),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    path: ["confirm"],
    message: "Passwords don't match",
  });

function SetupPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const setUser = useSessionStore((s) => s.setUser);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { displayName: "", email: "", password: "", confirm: "" },
  });

  async function onSubmit(values: z.infer<typeof schema>) {
    setError(null);
    try {
      const res = await authApi.setup(values.email, values.password, values.displayName);
      tokenStore.set(res.accessToken, res.refreshToken);
      markSignedIn();
      setUser(null);
      await router.invalidate();
      navigate({ to: "/" });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Setup failed");
    }
  }

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <BrandMark size={26} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Welcome to UDIP
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Create the first administrator account. Once this is done, authentication stays on for this
        deployment.
      </Typography>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Form form={form} onSubmit={onSubmit}>
        <Stack spacing={2}>
          <FormTextField control={form.control} name="displayName" label="Your name" required />
          <FormTextField control={form.control} name="email" label="Email" required />
          <FormTextField
            control={form.control}
            name="password"
            label="Password"
            type="password"
            required
          />
          <FormTextField
            control={form.control}
            name="confirm"
            label="Confirm password"
            type="password"
            required
          />
          <Button type="submit" variant="contained" disabled={form.formState.isSubmitting}>
            Create admin &amp; continue
          </Button>
        </Stack>
      </Form>
    </Stack>
  );
}
